import { Images } from '@/assets/images';
import { DocTypeEnum, GenderEnum, REGEX } from '@/constant';
import { convertNationCodeToName, dayjs, formatDate } from '@/utils';

import { range } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import { FormConfig } from '../define-document-write.type';

const initializeValue = {
	recommenderName: '',
	recommenderRegistrationNo: '',
	recommenderAddress: '',
	recommenderPhone: '',

	recommenderFormJsonHighlight: '',
	// List Recommender (10 rows to match the printed table)
	recommenderFormJson: range(0, 10).map((item) => ({
		id: item.toString(),
		nameInFull: '',
		nationality: '',
		gender: '',
		dateOfBirth: '',
		passportNo: '',
		relationToApplicant: '',
		addressHome: '',
	})),

	district: '',
	dateOfApplication: '',
	recommender: '',
	signature: '',
};

type IValues = typeof initializeValue;

export const INTRODUCTION_FOR_PARTICIPATION_IN_LABOR_UNION_CONFIG: FormConfig<
	IValues,
	DocTypeEnum.ARF_15
> = {
	initializeValue,
	title: '계절근로 참여 추천서(결혼이민자의 가족)',
	mapPropToValues({
		detailData,
		franchiseRecommenderListData,
		recommenderApplicantListData,
	}) {
		const { unitInfo } = detailData || {};
		// 추천자 detail box is sourced from the franchise recommender list API
		// (/worker/franchise/{id}/recommender/list).
		const recommenderInfo = franchiseRecommenderListData?.[0];

		// 추천대상자 명단 rows come from the recommender's applicant list API
		// (/worker/recommender/{id}/applicant/list).
		const createRecommenderChangeForm = (index: number) => {
			const worker = recommenderApplicantListData?.[index];
			return {
				id: index.toString(),
				nameInFull: worker?.name || '',
				nationality: worker?.nationalCode
					? convertNationCodeToName(worker.nationalCode)
					: '',
				gender:
					worker?.gender === GenderEnum.MALE
						? '남'
						: worker?.gender === GenderEnum.FEMALE
							? '여'
							: '',
				dateOfBirth: worker?.birthDate || '',
				passportNo: worker?.passportNo || '',
				relationToApplicant: worker?.recommenderRelationship || '',
				addressHome: worker?.hometownAddr || '',
			};
		};

		return {
			dateOfApplication: formatDate(dayjs()),
			district: unitInfo?.countyDistrict || '',
			recommenderName: recommenderInfo?.name || '',
			recommenderAddress: recommenderInfo?.address || '',
			recommenderPhone: recommenderInfo?.phone || '',
			recommenderRegistrationNo: isNotNilOrEmpty(recommenderInfo?.birthDate)
				? `${formatDate(recommenderInfo?.birthDate, {
						outputFormat: 'YYMMDD',
					})}-`
				: '',
			recommenderFormJson: range(0, 10).map(createRecommenderChangeForm),
			recommender: recommenderInfo?.name || '',
		};
	},
	pages: [
		{
			background: {
				src: Images.formIntroductionForParticipationInLaborUnion,
				className: 'template',
				dataSource: Images.formIntroductionForParticipationInLaborUnion,
			},
			textAreas: [
				{
					name: 'recommenderName',
					maxHeight: 48,
					className: 'text-[14px]',
					containerClassName: 'absolute top-[219px] left-[159px] w-[235px]',
					regex: REGEX.ALLOW_KR_EN,
					maxLength: 16,
				},
				{
					name: 'recommenderRegistrationNo',
					maxHeight: 48,
					maxLength: 20,
					containerClassName: 'absolute top-[219px] left-[570px] w-[124px]',
					regex: REGEX.ALLOW_NUMBER_DASH,
					className: 'text-[14px] leading-none',
				},
				{
					name: 'recommenderAddress',
					maxHeight: 48,
					maxLength: 45,
					containerClassName: 'absolute top-[271px] left-[159px] w-[235px]',
					className: 'leading-none text-[14px]',
				},
				{
					name: 'recommenderPhone',
					maxHeight: 48,
					className: 'text-[14px]',
					containerClassName: 'absolute top-[271px] left-[570px] w-[124px]',
					maxLength: 14,
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'district',
					maxHeight: 20,
					maxLength: 10,
					containerClassName: 'absolute top-[844px] left-[303px] w-[76px]',
					className: 'leading-none',
				},
				{
					name: 'district',
					maxHeight: 20,
					maxLength: 10,
					containerClassName: 'absolute top-[870px] left-[517px] w-[104px]',
					className: 'leading-none',
				},
				{
					name: 'recommender',
					maxHeight: 42,
					className: 'text-[14px]',
					containerClassName: 'absolute top-[943px] left-[245px] w-[243px]',
					regex: REGEX.ALLOW_KR_EN,
					maxLength: 16,
				},
				(data, index) => ({
					jsonKey: 'recommenderFormJson',
					name: 'nameInFull',
					tabIndex: index * 7 + 1,
					repeat: 10,
					maxHeight: 35,
					maxLength: 36,
					containerClassName: 'absolute text-center left-[101px] w-[120px]',
					'data-tabIndex': index,
					boxStyle: {
						top: `${431 + 40 * index}px`,
					},
					style: { fontSize: 12, lineHeight: '10px' },
				}),
				(data, index) => ({
					jsonKey: 'recommenderFormJson',
					name: 'nationality',
					tabIndex: index * 7 + 1,
					repeat: 10,
					maxHeight: 35,
					maxLength: 20,
					containerClassName: 'absolute text-center left-[226px] w-[60px]',
					'data-tabIndex': index,
					boxStyle: {
						top: `${431 + 40 * index}px`,
					},
					style: { fontSize: 12, lineHeight: '10px' },
				}),
				(data, index) => ({
					jsonKey: 'recommenderFormJson',
					name: 'gender',
					tabIndex: index * 7 + 1,
					repeat: 10,
					maxHeight: 35,
					maxLength: 10,
					containerClassName: 'absolute text-center left-[290px] w-[50px]',
					'data-tabIndex': index,
					boxStyle: {
						top: `${431 + 40 * index}px`,
					},
					style: { fontSize: 12, lineHeight: '10px' },
				}),
				(data, index) => ({
					jsonKey: 'recommenderFormJson',
					name: 'dateOfBirth',
					tabIndex: index * 7 + 1,
					repeat: 10,
					maxHeight: 35,
					maxLength: 10,
					containerClassName: 'absolute text-center left-[345px] w-[70px]',
					'data-tabIndex': index,
					boxStyle: {
						top: `${431 + 40 * index}px`,
					},
					style: { fontSize: 12, lineHeight: '10px' },
				}),
				(data, index) => ({
					jsonKey: 'recommenderFormJson',
					name: 'passportNo',
					tabIndex: index * 7 + 1,
					repeat: 10,
					maxHeight: 35,
					maxLength: 20,
					containerClassName: 'absolute text-center left-[421px] w-[62px]',
					'data-tabIndex': index,
					boxStyle: {
						top: `${431 + 40 * index}px`,
					},
					style: { fontSize: 12, lineHeight: '10px' },
				}),
				(data, index) => ({
					jsonKey: 'recommenderFormJson',
					name: 'relationToApplicant',
					tabIndex: index * 7 + 1,
					repeat: 10,
					maxHeight: 35,
					maxLength: 40,
					containerClassName: 'absolute text-center left-[487px] w-[89px]',
					'data-tabIndex': index,
					boxStyle: {
						top: `${431 + 40 * index}px`,
					},
					style: { fontSize: 12, lineHeight: '10px' },
				}),
				(data, index) => ({
					jsonKey: 'recommenderFormJson',
					name: 'addressHome',
					tabIndex: index * 7 + 1,
					repeat: 10,
					maxHeight: 35,
					maxLength: 40,
					containerClassName: 'absolute text-center left-[580px] w-[112px]',
					'data-tabIndex': index,
					boxStyle: {
						top: `${431 + 40 * index}px`,
					},
					style: { fontSize: 12, lineHeight: '10px' },
				}),
			],
			datePickers: [
				{
					name: 'dateOfApplication',
					className:
						'[&>.ant-picker-input>input]:!text-base [&>.ant-picker-input>input]:!w-[140px]',
					format: 'YYYY.MM.DD',
					containerClassName: 'absolute top-[952px] left-[80px]',
				},
			],
			signatures: [
				{
					name: 'signature',
					className: 'absolute w-[206px] h-[42px] top-[943px] left-[493px]',
				},
			],
			highlightBoxes: [
				{
					name: 'recommenderName',
					className: 'top-[224px] left-[83px] w-[55px]',
				},
				{
					name: 'recommenderRegistrationNo',
					className: 'top-[218px] left-[411px] w-[146px] !h-[38px]',
				},
				{
					name: 'recommenderAddress',
					className: 'top-[276px] left-[83px] w-[55px]',
				},
				{
					name: 'recommenderPhone',
					className: 'top-[276px] left-[455px] w-[55px]',
				},
				{
					name: 'recommenderFormJsonHighlight',
					className: 'top-[382px] left-[134px] w-[55px]',
				},
				{
					name: 'recommenderFormJsonHighlight',
					className: 'top-[382px] left-[228px] w-[55px]',
				},
				{
					name: 'recommenderFormJsonHighlight',
					className: 'top-[382px] left-[297px] w-[40px]',
				},
				{
					name: 'recommenderFormJsonHighlight',
					className: 'top-[382px] left-[353px] w-[55px]',
				},
				{
					name: 'recommenderFormJsonHighlight',
					className: 'top-[382px] left-[424px] w-[55px]',
				},
				{
					name: 'recommenderFormJsonHighlight',
					className: 'top-[375px] left-[495px] w-[72px] !h-[39px]',
				},
				{
					name: 'recommenderFormJsonHighlight',
					className: 'top-[382px] left-[604px] w-[65px]',
				},
				{
					name: 'district',
					className: 'top-[836px] left-[303px] w-[76px]',
				},
				{
					name: 'district',
					className: 'top-[863px] left-[517px] w-[105px]',
				},
				{
					name: 'dateOfApplication',
					className: 'top-[902px] left-[121px] w-[65px]',
				},
				{
					name: 'recommender',
					className: 'top-[902px] left-[335px] w-[65px]',
				},
				{
					name: 'signature',
					className: 'top-[902px] left-[549px] w-[93px]',
				},
			],
		},
	],
};
